import {Empty} from 'antd';
import {cloneDeep, merge} from 'lodash';
import {useMemo} from 'react';

import {DataLoadingSwitch} from '../../../../../../../component/loading-switch/loading-switch';
import {BarChart} from '../../../../../../../layout/chart/bar-chart/bar-chart';
import {barChartOptions} from '../../../../../../../layout/chart/bar-chart/bar-chart-helper';
import {BarDataType, ChartJsBarOptionsType} from '../../../../../../../layout/chart/bar-chart/bar-chart-type';
import {getDefaultReactTooltipOptions} from '../../../../../../../layout/chart/chart-const';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useReviewAnalyticsAspects} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-hook';
import * as paletteStyles from '../../../../reviews-analysis-palette-export.scss';

// The chart shows every aspect (unlike the paginated table), so fetch a single page large
// enough to cover the whole bounded aspect taxonomy.
const ASPECTS_DISTRIBUTION_PAGE_SIZE = 100;
const CHART_MIN_HEIGHT = 320;
const CHART_HEIGHT_PER_ASPECT = 44;

function getChartOptions(data: BarDataType): ChartJsBarOptionsType {
    // Reuse the shared stacked-bar options, but flip to horizontal: x becomes the
    // review-count value axis, y carries the aspect labels; legend keeps neg→neu→pos order.
    return merge(cloneDeep(barChartOptions), {
        indexAxis: 'y' as const,
        scales: {
            x: {
                type: 'linear' as const,
                beginAtZero: true,
                grid: {display: true},
            },
        },
        plugins: {
            legend: {reverse: false},
            tooltip: getDefaultReactTooltipOptions({data}),
        },
    });
}

// «Распределение количества отзывов по аспектам»: horizontal stacked bar of per-aspect
// review counts split by sentiment (negative / neutral / positive).
export function AspectsReviewsDistributionChart(): JSX.Element {
    const {getLocalizedString} = useLocale();

    const {data, isLoading, isError} = useReviewAnalyticsAspects({
        initialPageSize: ASPECTS_DISTRIBUTION_PAGE_SIZE,
        shouldSaveState: false,
    });

    const results = data?.results ?? [];

    const chartData: BarDataType = useMemo(
        () => ({
            labels: results.map((record) => record.name),
            datasets: [
                {
                    label: getLocalizedString(
                        'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEGATIVE'
                    ),
                    values: results.map((record) => record.negativeReviews.value),
                    color: paletteStyles.sentiment_negative,
                    stack: 'reviews',
                },
                {
                    label: getLocalizedString(
                        'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__NEUTRAL'
                    ),
                    values: results.map((record) => record.neutralReviews.value),
                    color: paletteStyles.sentiment_neutral,
                    stack: 'reviews',
                },
                {
                    label: getLocalizedString(
                        'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION__POSITIVE'
                    ),
                    values: results.map((record) => record.positiveReviews.value),
                    color: paletteStyles.sentiment_positive,
                    stack: 'reviews',
                },
            ],
        }),
        [results, getLocalizedString]
    );

    const options = useMemo(() => getChartOptions(chartData), [chartData]);

    return (
        <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE_DISTRIBUTION" />}>
            <DataLoadingSwitch
                emptyImage={Empty.PRESENTED_IMAGE_SIMPLE}
                isEmpty={results.length === 0}
                isError={isError}
                isLoading={isLoading}
            >
                <div style={{height: Math.max(CHART_MIN_HEIGHT, results.length * CHART_HEIGHT_PER_ASPECT)}}>
                    <BarChart data={chartData} options={options} />
                </div>
            </DataLoadingSwitch>
        </PageCard>
    );
}
